import type { AuthTokens, AuthUser } from '@cf/shared';
import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import i18n, { hasLocalLocaleChoice } from '../i18n';
import { disablePush } from './push';
import { api, refreshSession, session } from './api';

interface AuthState {
  user: AuthUser | null;
  /** True until the initial silent refresh has completed. */
  restoring: boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [restoring, setRestoring] = useState(true);
  const queryClient = useQueryClient();

  const applyTokens = useCallback((tokens: AuthTokens | null) => {
    session.set(tokens?.accessToken ?? null);
    setUser(tokens?.user ?? null);
    // The profile locale is only a default: a language picked on this device wins.
    if (tokens?.user.locale && !hasLocalLocaleChoice() && tokens.user.locale !== i18n.language) {
      void i18n.changeLanguage(tokens.user.locale);
    }
  }, []);

  useEffect(() => {
    session.onLost(() => applyTokens(null));
    // Restore the session from the refresh cookie on page load.
    void refreshSession()
      .then(applyTokens)
      .finally(() => setRestoring(false));
  }, [applyTokens]);

  const login = useCallback(
    async (email: string, password: string) => {
      const tokens = await api<AuthTokens>('/auth/login', {
        method: 'POST',
        json: { email, password },
      });
      applyTokens(tokens);
      return tokens.user;
    },
    [applyTokens],
  );

  const logout = useCallback(async () => {
    try {
      // A signed-out phone must stop receiving the owner's alerts.
      await disablePush().catch(() => undefined);
      await api('/auth/logout', { method: 'POST' });
    } finally {
      applyTokens(null);
      queryClient.clear();
    }
  }, [applyTokens, queryClient]);

  const value = useMemo(
    () => ({ user, restoring, login, logout }),
    [user, restoring, login, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
